import { Link, useRouter } from 'expo-router'
import type { ReactNode } from 'react'

import { Message, type TMessageProps } from '@/src/elements/message'
import { INTENTION_TYPE } from '@/src/features/intention/models/Intention.enums'
import { useLastBirthdayDate } from '@/src/features/intention/utils/useLastBirthdayDate'
import type { TNotificationApi } from '@/src/features/notification/api/types/notificationApi.types'
import { NOTIFICATION_TYPE } from '@/src/features/notification/models/notification.constants'
import { useMarkNotificationAsReadMutation } from '@/src/features/notification/services/useMarkNotificationAsReadMutation'
import { ICON } from '@/src/general/enums/icons'
import { formatCalendar } from '@/src/general/primitives/dates/dayjs'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate, type TMessageKey } from '@/src/lang/useTranslate'

type TIntentionNotificationMessageProps = TMessageProps & {
  notification: TNotificationApi<
    | NOTIFICATION_TYPE.INTENTION_FINISHED
    | NOTIFICATION_TYPE.BIRTHDAY_INTENTION_FINISHED
    | NOTIFICATION_TYPE.ARRIVAL_INTENTION_FINISHED
  >
}

const getMessageData = ({
  type,
  payload,
}: TIntentionNotificationMessageProps['notification']): {
  intentionId: number
  intentionType: INTENTION_TYPE
  messageTextKey: TMessageKey
  iconName: ICON
} => {
  switch (type) {
    case NOTIFICATION_TYPE.INTENTION_FINISHED:
      return {
        intentionId: payload.intention.id,
        intentionType: INTENTION_TYPE.INTENTION,
        iconName: ICON.CLOCK_CHECK,
        messageTextKey: 'intention.finished.intention',
      }
    case NOTIFICATION_TYPE.BIRTHDAY_INTENTION_FINISHED:
      return {
        intentionId: payload.birthdayIntention.id,
        intentionType: INTENTION_TYPE.BIRTHDAY,
        iconName: ICON.BIRTHDAY,
        messageTextKey: 'intention.finished.birthday',
      }
    case NOTIFICATION_TYPE.ARRIVAL_INTENTION_FINISHED:
      return {
        intentionId: payload.intention.id,
        intentionType: INTENTION_TYPE.ARRIVAL,
        iconName: ICON.FACE_HAPPY,
        messageTextKey: 'intention.finished.arrival',
      }
  }
}

export const IntentionNotificationMessage = ({
  notification,
  ...otherProps
}: TIntentionNotificationMessageProps): ReactNode => {
  const { t } = useTranslate()
  const { push } = useRouter()
  const { mutate: markAsRead } = useMarkNotificationAsReadMutation()
  const { routes } = useRoutes()
  const lastBirthdayDate = useLastBirthdayDate()
  const { intentionId, intentionType, iconName, messageTextKey } = getMessageData(notification)
  const { prayersCount } = notification.payload

  const href = routes.intentionByIdPrayers(intentionId, intentionType)

  return prayersCount > 0 ? (
    <Message
      key={notification.id}
      colors="secondary"
      iconName={iconName}
      buttons={[
        {
          onPress: () => push(href),
          children: t('intention.finished.thank'),
          className: 'w-full',
        },
      ]}
      onClose={() => markAsRead(notification.id)}
      {...otherProps}
    >
      {t(messageTextKey, {
        count: notification.payload.prayersCount,
        birthdayDate: lastBirthdayDate ? formatCalendar(lastBirthdayDate?.format('YYYY-MM-DD'), t, true) : '',
        link: (chunks) => (
          <Link key="notification-link" className="underline" href={href}>
            {chunks}
          </Link>
        ),
      })}
    </Message>
  ) : null
}
